<template>
  <f7-page>
    <f7-navbar>
      <f7-nav-left>
        <f7-link @click="checkForChanges">
          <i class="icon icon-back"/>
          <span v-if="$theme.ios">{{$t('action.back')}}</span></f7-link>
      </f7-nav-left>
      <f7-nav-title>{{$t('title.transactionDetails')}}</f7-nav-title>
    </f7-navbar>
    <f7-block class="transaction-header text-align-center">
      <h1>{{formatMoney(transaction.currency, transaction.amount)}}</h1>
      <p>{{transaction.date | toWeekDate}}<br>{{transaction.date | toDayTime}}</p>
      <h4>{{transaction.description}}</h4>
      <img src="~@/assets/img/Aio-Dark-Transparent-Marque-Vector.svg" alt="aio* marque">
    </f7-block>
    <f7-list inline-labels>
      <f7-list-item v-if="hasReceipt" :title="$t('label.receipt')" swipeout @click="viewerOpened = true"
                    :after="$t('action.viewReceipt')"
      >
        <f7-swipeout-actions right>
          <f7-swipeout-button @click="deleteReceipt(receipt.id)" color="pink" confirm confirmText="" close>
            {{$t('action.delete')}}
          </f7-swipeout-button>
        </f7-swipeout-actions>
      </f7-list-item>
      <f7-list-item v-else :title="$t('label.receipt')" :after="$t('action.addReceipt')" link=""
                    popover-open=".add-receipt"
      />
      <f7-list-item :title="$t('label.category')" smart-select :smart-select-params="categorySelectParams"
                    ref="selectCategory"
      >
        <select name="category" v-model="transaction.categoryId">
          <optgroup v-for="mainCategory in categories" :label="mainCategory.name" :key="mainCategory.id">
            <option v-for="subCategory in mainCategory.categories" :value="subCategory.id"
                    :data-display-as="fullCategoryName(mainCategory.name, subCategory)"
                    :selected="transaction.categoryId === subCategory.id" :key="subCategory.id"
            >
              {{subCategory | categoryName}}
            </option>
          </optgroup>
        </select>
      </f7-list-item>
      <f7-list-item :title="$t('label.request')" smart-select :smart-select-params="requestSelectParams"
                    ref="selectRequest"
                    :disabled="wasRefunded"
      >
        <select name="request" v-model="transaction.requestId">
          <optgroup v-for="budget in activeBudgets" :label="budget.name" :key="budget.potId">
            <option v-for="request in requestsForBudget(budget.potId)" :value="request.id"
                    :data-display-as="fullRequestName(request)"
                    :selected="transaction.requestId === request.id" :key="request.id"
            >
              {{requestName(request)}}
            </option>
          </optgroup>
        </select>
      </f7-list-item>
      <f7-list-input :label="$t('label.notes')"
                     type="text"
                     :placeholder="$t('placeholder.enterNotes')"
                     :value="transaction.notes"
                     @input="transaction.notes = $event.target.value"
      />
      <f7-block>
        <f7-button large fill :class="{'disabled': !edited}" @click.prevent="save">{{$t('action.saveChanges')}}
        </f7-button>
      </f7-block>
    </f7-list>
    <f7-popover class="add-receipt">
      <f7-list>
        <f7-list-item v-if="hasCamera">
          <f7-link @click="openCamera" popover-close>{{$t('action.takePicture')}}</f7-link>
        </f7-list-item>
        <f7-list-item>
          <f7-link @click="$refs.fileUpload.click()" popover-close>{{$t('action.selectFile')}}</f7-link>
          <input ref="fileUpload" class="fileUpload" type="file" @change="uploadFile">
        </f7-list-item>
        <f7-list-item>
          <f7-link v-if="hasLooseReceipts" @click="viewerOpened = true" popover-close>{{$t('action.chooseEmailed')}}
          </f7-link>
          <span v-else class="mail-info">
            {{$t('text.forwardEmail[0]')}}
            <f7-link external href="mailto:receipts@getaio.co.uk">receipts@getaio.co.uk</f7-link>
            {{$t('text.forwardEmail[1]')}}
          </span>
        </f7-list-item>
      </f7-list>
    </f7-popover>
    <receipt-viewer :opened="viewerOpened" @closed="viewerOpened = false" :showAdd="!hasReceipt"
                    @delete-receipt="deleteReceipt" @attach-receipt="attachReceipt"
                    :receipts="hasReceipt ? currentReceipt : looseReceipts"
    />
  </f7-page>
</template>

<script>
  import api from 'aio-api'
  import cordovaHelpers from '@/utils/cordova-helpers'
  import helpers from '@/utils/helpers'
  import ReceiptViewer from '../components/receipt-viewer'

  export default {
    name: 'transaction-page',
    components: {ReceiptViewer},
    computed: {
      currentReceipt () {
        return this.receipt ? [this.receipt] : []
      },
      hasLooseReceipts () {
        return this.looseReceipts.length > 0
      },
      hasReceipt () {
        return !!this.transaction.receiptId
      },
      wasRefunded () {
        const r = this.$store.getters['requests/getById'](this.transaction.requestId)
        return r ? r.wasRefunded : false
      },
    },
    async created () {
      const expenseId = this.$f7route.params.id
      this.transaction = await api.getExpense(expenseId)
      if (this.hasReceipt) {
        this.initialReceiptId = this.transaction.receiptId
        this.initReceipt()
      }
      await this.$store.dispatch('fetchCategories')
      const catName = this.transaction.categoryId ? this.getFullCategoryName(this.transaction.categoryId) : this.$t('action.tagCategory')
      this.$refs.selectCategory.$el.querySelector('.item-after').innerHTML = catName
      await this.$store.dispatch('budgets/fetch')
      await this.$store.dispatch('requests/fetchAll')
      const requestName = this.transaction.requestId ? this.getFullRequestName(this.transaction.requestId) : this.$t('action.tagRequest')
      this.$refs.selectRequest.$el.querySelector('.item-after').innerHTML = requestName
      this.$store.dispatch('fetchReceipts')
      this.edited = false
    },
    data () {
      return {
        categorySelectParams: {
          appendSearchbarNotFound: true,
          closeOnSelect: true,
          openIn: 'popup',
          pageTitle: this.$t('action.selectCategory'),
          searchbar: true,
          searchbarPlaceholder: this.$t('action.searchCategory'),
        },
        edited: false,
        initialReceiptId: false,
        receipt: undefined,
        requestSelectParams: {
          appendSearchbarNotFound: true,
          closeOnSelect: true,
          openIn: 'popup',
          pageTitle: this.$t('action.selectRequest'),
          searchbar: true,
          searchbarPlaceholder: this.$t('action.searchRequest'),
        },
        transaction: {},
        viewerOpened: false,
      }
    },
    filters: {
      categoryName (category) {
        return category.code ? `${category.name} (${category.code})` : category.name
      },
    },
    methods: {
      attachReceipt (receiptId) {
        this.$set(this.transaction, 'receiptId', receiptId)
        this.initReceipt()
      },
      checkForChanges () {
        if (this.edited) {
          const self = this
          this.$f7.dialog.create({
            text: this.$t('dialog.saveChanges'),
            title: '',
            buttons: [
              {
                text: this.$t('action.discard'),
                async onClick () {
                  if (self.receipt && self.receipt.id !== self.initialReceiptId) await api.deleteReceipt(self.receipt.id)
                  self.$f7router.back()
                },
              },
              {
                text: this.$t('action.save'),
                onClick () {
                  self.save()
                  self.$f7router.back()
                },
              },
            ],
          }).open()
        } else this.$f7router.back()
      },
      deleteReceipt (receiptId) {
        this.$f7.dialog.confirm('', this.$t('dialog.deleteReceipt'), async () => {
          try {
            await api.deleteReceipt(receiptId)
            await this.$store.dispatch('fetchReceipts')
            if (receiptId === this.transaction.receiptId) {
              await this.$delete(this.transaction, 'receiptId')
              this.viewerOpened = false
              this.save()
            } else this.viewerOpened = this.looseReceipts.length > 0
          } catch (error) {
            //TODO: i18n error message
            this.$f7.dialog.alert(error.response.data, '')
          }
        })
      },
      fullCategoryName (mainCategoryName, category) {
        return `${this.$options.filters.categoryName(category)} - ${mainCategoryName}`
      },
      fullRequestName (request, budgetName) {
        if (budgetName) return `${this.formatMoney(request.currency, request.amount)} for ${request.reason} from ${budgetName}`
        else return `${this.formatMoney(request.currency, request.amount)} for ${request.reason}`
      },
      getFullCategoryName (categoryId) {
        const mainCategory = this.categories.find(m => m.categories.find(s => s.id === categoryId))
        return this.fullCategoryName(mainCategory.name, mainCategory.categories.find(s => s.id === categoryId))
      },
      getFullRequestName (requestId) {
        const myRequest = this.myProcessedRequests.find(r => r.id === requestId)
        const request = myRequest ? myRequest : this.processedRequests.find(r => r.id === requestId)
        const budget = this.budgets.find(b => b.potId === request.potId)
        const budgetName = budget ? budget.name : undefined
        return this.fullRequestName(request, budgetName)
      },
      async initReceipt () {
        this.receipt = await api.getReceipt(this.transaction.receiptId)
        this.$store.dispatch('fetchReceipts')
      },
      openCamera () {
        const self = this

        async function onSuccess (imageData) {
          const formData = new FormData()
          const byteCharacters = atob(imageData)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const imgBlob = new Blob([byteArray], {type: 'image/jpeg'})
          formData.append('receipt', imgBlob, 'receipt.jpg')
          const receiptId = await api.postReceipt(self.currentUserId, formData)
          self.$set(self.transaction, 'receiptId', receiptId)
          self.initReceipt()
        }

        function onFail (message) {
          if (message !== 'No Image Selected') self.$f7.dialog.alert(message, '')
        }

        navigator.camera.getPicture(onSuccess, onFail, {
          quality: 50,
          destinationType: window.Camera.DestinationType.DATA_URL,
        })
      },
      requestsForBudget (budgetId) {
        return this.myProcessedRequests.filter(r => {
          const wasRefunded = r.wasRefunded ? r.wasRefunded : false
          return r.potId === budgetId && r.status === 'approved' && !wasRefunded
        })
      },
      requestName (request) {
        return `${request.reason} (${this.formatMoney(request.currency, request.amount)}) on ${this.toDate(request.processDate)}`
      },
      async save () {
        try {
          await api.updateTransaction(this.transaction.id, this.transaction)
          this.$bus.emit('transaction-update')
          this.edited = false
        } catch (error) {
          //TODO: i18n error message
          this.$f7.dialog.alert(error.response.data, '')
        }
      },
      async uploadFile (e) {
        const formData = new FormData()
        formData.append('receipt', e.target.files[0])
        const receiptId = await api.postReceipt(this.currentUserId, formData)
        this.$set(this.transaction, 'receiptId', receiptId)
        this.initReceipt()
      },
    },
    mixins: [cordovaHelpers, helpers],
    watch: {
      transaction: {
        handler () {
          this.edited = true
        },
        deep: true,
      },
    },
  }
</script>

<style lang="scss">
  @import '@/sass/variables.scss';

  .transaction-header {
    img {
      max-width: 40px;
    }
  }

  .smart-select-popup {
    .navbar .title {
      color: $white;
    }

    li.disabled {
      display: none;
    }
  }

  .fileUpload {
    display: none;
  }

  .mail-info {
    color: #6d6d72;
    font-size: 12px;
  }
</style>
